import { BulkAddCommService } from './../../common/facet/bulk-add-comm.service';
import { FacetLoadingStateService } from './../../common/facet/facet-loading-state.service';
import { BulkAddResult } from '../../common/facet/models/bulk-edit.classes';
import { BulkAddExitReason } from '../../common/facet/models/bulk-add-exit-reason.enum';
import { BulkEditSection } from '../../common/facet/models/bulk-edit-section.enum';
import { BulkEditOptions } from '../../common/facet/models';
import {
    AfterViewInit,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';

import { AnimalService } from '../services/animal.service';
import { EnumerationService } from '../../enumerations/enumeration.service';
import { JobService } from '../../jobs/job.service';
import { LineService } from '../../lines/line.service';
import { LocationService } from '../../locations/location.service';
import { MaterialService } from '../../services/material.service';
import { MaterialPoolService } from '../../services/material-pool.service';
import { NamingService } from '../../services/naming.service';
import { VocabularyService } from '../../vocabularies/vocabulary.service';
import { AnimalLogic } from '../animal-logic.shared';
import {
  empty,
    notEmpty,
    sortObjectArrayByProperty,
    testBreezeIsNew,
} from '../../common/util';
import { GenotypeService } from '../../genotypes/genotype.service';
import { SettingService } from '../../settings/setting.service';
import {
    Animal,
    BulkCreateAnimal,
    BulkCreateAnimalRequestBody,
    Entity,
    Line,
    TaxonCharacteristic,
    cv_AnimalMatingStatus,
    cv_AnimalStatus,
    MaterialPool
} from '@common/types';
import { FacetView, IFacet } from '@common/facet';
import { IValidatable, SaveChangesService } from '@services/save-changes.service';
import { ToastrService } from '@services/toastr.service';
import { LogLevel } from '@services/models';
import { TranslationService } from '@services/translation.service';
import { NgModel } from '@angular/forms';
import { AnimalBulkTemplatesComponent } from './animal-bulk-templates.component';
import {LoggingService} from '@services/logging.service';

class BulkHousingUnit {
    // Housing ID
    id: string;
    // Number of housed animals
    size: number;

    constructor(size: number) {
        this.size = size;
    }
}

export class BulkHousing {
    // External State
    //
    // Number of animals, updated by the bulk-add component
    itemsToAdd = 1;
    // Is Housing auto-naming active?
    namingActive = false;

    // Internal State
    //
    // Has the Create Housing section been initialized?
    enabled = false;
    // Number of unhoused animals
    unhoused = 0;
    // Housing unit config (size, ID)
    units: BulkHousingUnit[] = new Array<BulkHousingUnit>();

    // Form Elements
    //
    // Are new housing units being created?
    add = false;
    // Number of animals per unit
    size = 5;
    // Housing Type
    materialPoolTypeKey: number;
    materialPoolTypes: any[] = [];
    // Date
    date: any = new Date();
    // Owner
    owner: any;
    // Location (read-only)
    location: any;
    // Container Type
    containerTypeKey: number;
    containerTypes: any[] = [];

    materialPoolStatusKey: number;
}

@Component({
    selector: 'animal-bulk-add',
    templateUrl: './animal-bulk-add.component.html',
    providers: [
        BulkAddCommService
    ],
    styles: [`
      .housing .form-group .col-md-3 {
         text-align: right;
         padding-right: 0;
      }
      .housing-size {
          direction: rtl;
          padding: 3px 5px;
          width: 50px;
       }
      .housing-units,
      .housing-units .input-group {
          margin-bottom: 5px;
      }
      .housing-unit-id    { min-width: 100px; max-width: 100px; }
      .housing-unit-size  { min-width:  50px; max-width:  50px; }
    `],
})
export class AnimalBulkAddComponent implements OnInit, OnDestroy, AfterViewInit, IValidatable {
    @ViewChild('bulkTemplates') bulkTemplates: AnimalBulkTemplatesComponent;
    @ViewChild('dateControl') dateControl: NgModel;

    @Input() facet: IFacet;
    @Input() facetView: FacetView;
    // Active and required fields set by facet settings
    @Input() activeFields: string[];
    @Input() requiredFields: string[];

    @Output() exit: EventEmitter<BulkAddResult> = new EventEmitter<BulkAddResult>();

    animals: Entity<Animal>[] = [];
    housings: any[] = [];
    animalNamingActive = false;

    // shared logic for animal facet
    animalLogic: AnimalLogic;
    bulkHousing: BulkHousing;
    
    readonly COMPONENT_LOG_TAG = 'animal-bulk-add';

    BulkEditSection = BulkEditSection;

    animalContainerDefaultKey: any = null;

    constructor(
        private animalService: AnimalService,
        private bulkAddCommService: BulkAddCommService,
        private enumerationService: EnumerationService,
        private faceLoadingStateService: FacetLoadingStateService,
        private jobService: JobService,
        private lineService: LineService,
        private locationService: LocationService,
        private materialService: MaterialService,
        private materialPoolService: MaterialPoolService,
        private namingService: NamingService,
        private vocabularyService: VocabularyService,
        private genotypeService: GenotypeService,
        private settingService: SettingService,
        private saveChangesService: SaveChangesService,
        private toastrService: ToastrService,
        private translationService: TranslationService,
        private loggingService: LoggingService,

    ) {
        this.animalLogic = new AnimalLogic(
            animalService,
            enumerationService,
        );
    }

    // lifecycle
    ngOnInit() {
        this.saveChangesService.registerValidator(this);
        this.initialize();
    }

    ngOnDestroy() {
        this.saveChangesService.unregisterValidator(this);
    }

    ngAfterViewInit(): void {
        if (this.bulkTemplates?.bulkOptions) {
            this.bulkTemplates.bulkOptions.saveButtonValidators.push(() => {
                return this.animalNamingActive;
            });
        }
    }

    initialize() {
        // Check if animal is automatically named. May need to be moved to more appropriate spot.
        this.namingService.isAnimalNamingActive().then((active: boolean) => {
            this.animalNamingActive = active;
        });
        this.initBulkHousing();
    }

    /**
     * The "Save and Clear Form" button has been used.
     * 
     * @param options
     */
    formClear(options: BulkEditOptions) {
        // Reset the Bulk Housing
        this.initBulkHousing();
        options.__addObject.DateOrigin = new Date();
    }

    async saveClicked(result: BulkAddResult) {
        result.newItems = [];
        const values = await this.vocabularyService.getCVByFieldEquals(
            'cv_AnimalStatuses', 
            'C_AnimalStatus_key', 
            result.initialValues.C_AnimalStatus_key, 
            false
        ) as any;
        if (!result.initialValues.Material.C_Line_key &&
            result.reason !== BulkAddExitReason.Edit &&
            result.reason !== BulkAddExitReason.AddAndEdit &&
            result.reason !== BulkAddExitReason.Cancel) {
            const errorMessage = `An Animal requires a ${this.translationService.translate('Line')}.`;
            this.toastrService.showToast(this.saveChangesService.generateSaveErrorMessage(this.facet.FacetName, this.facetView, errorMessage), LogLevel.Error);
            this.bulkAddCommService.saveCanceled();
        } else if (values && values.IsExitStatus && this.bulkHousing.add) {
            this.toastrService.showToast("Animals in an 'End-State' Status cannot be housed. Please change the status or deselect the 'Create new housing units' option.", LogLevel.Warning);
            this.bulkAddCommService.saveCanceled();
            return;
        }
        switch (result.reason) {
            case BulkAddExitReason.Cancel:
                // do nothing on cancel
                return;
            case BulkAddExitReason.Save:
                await this.bulkAddAndSave(result);
                result.newItems = this.animals;
                break;
            case BulkAddExitReason.Edit:
                this.animals = await this.createNewAnimals(result.numberItemsToAdd, result.initialValues);
                this.housings = await this.createNewHousings(this.bulkHousing, this.animals);
                result.newItems = this.animals.concat(this.housings);
                result.reason = BulkAddExitReason.AddAndEdit;
                break;
        }
        
        this.bulkAddCommService.saveComplete();
    }
    
    private async bulkAddAndSave(result: BulkAddResult) {
        try {
            this.animals = await this.createNewAnimals(result.numberItemsToAdd, result.initialValues);
            this.housings = await this.createNewHousings(this.bulkHousing, this.animals);
            const errorMessage = await this.validate();
            if (errorMessage) {
                this.toastrService.showToast(this.saveChangesService.generateSaveErrorMessage(this.facet.FacetName, this.facetView, errorMessage),
                    LogLevel.Error);
                this.discardChanges(false);
                return;
            }
            await this.animalService.bulkCreate(this.animals, this.housings);
        } catch (error) {
            for (const animal of this.animals) {
                this.animalService.cancelAnimal(animal);
            }
            for (const housing of this.housings) {
                this.materialPoolService.cancelMaterialPool(housing);
            }
            this.bulkAddCommService.saveCanceled();
        }
    }

    async validate(): Promise<string> {
        const translatedAnimal = this.translationService.translate('Animal');

        let errorMessage = this.bulkTemplates?.validate?.() ?? '';

        for (const animal of this.animals) {
            if (this.animalNamingActive && testBreezeIsNew(animal)) {
                const invalidField = await this.animalService.validateAnimalNamingField(animal);
                if (invalidField) {
                    return `The ${this.translationService.translate(invalidField)} field is required for automatic naming.`;
                }
            } else if (empty(animal.AnimalName)) {
                return `A ${translatedAnimal} requires a Name.`;
            }

            if (!animal.Material.C_Line_key) {
                return `An ${translatedAnimal} requires a ${this.translationService.translate('Line')}.`;
            }

            errorMessage = await this.settingService.validateRequiredFields(this.requiredFields, animal, 'animal');
            if (errorMessage) {
                break;
            }
        }

        return errorMessage;
    }

    exitClicked(result: BulkAddResult) {
        if (result.reason === BulkAddExitReason.Cancel) {
           this.discardChanges();
        }
        this.exit.emit(result);
    }

    private discardChanges(notifyOnSuccess = true) {
        this.animalService.cancelBulkChanges(this.animals, this.housings);
        // remove added, but un-saved animals and housings
        this.animals = this.animals.filter((animal) => {
            return animal.C_Material_key > 0;
        });
        this.housings = this.housings.filter((housing) => {
            return housing.C_MaterialPool_key > 0;
        });
        if (notifyOnSuccess) {
            this.loggingService.logFacetUndoSuccess(this.COMPONENT_LOG_TAG);
        }
    }

    async createNewAnimals(numberItemsToAdd: number, initialValues: BulkCreateAnimalRequestBody): Promise<Entity<Animal>[]> {
        const animals: Entity<Animal>[] = [];
        const promises: Promise<unknown>[] = [];

        // Get all defaults for using
        const characteristicsDefaults = {};
        if (notEmpty(initialValues.TaxonCharacteristicInstance)) {
            for (const characteristic of initialValues.TaxonCharacteristicInstance) {
                if (characteristic.CharacteristicValue) {
                    characteristicsDefaults[characteristic.C_TaxonCharacteristic_key] = characteristic.CharacteristicValue;
                }
            }
        }
        initialValues.characteristicsDefaults = characteristicsDefaults;
        
        if (!initialValues.C_AnimalStatus_key) {
            promises.push(this.vocabularyService.getCVDefault('cv_AnimalStatuses')
                .then((value: cv_AnimalStatus) => {
                    initialValues.cv_AnimalStatus = value;
                }));
        }

        promises.push(this.vocabularyService.getCVDefault('cv_AnimalMatingStatuses')
            .then((value: cv_AnimalMatingStatus) => {
                initialValues.cv_AnimalMatingStatus = value;
            }));

        // Get line default stuff
        if (initialValues.Material.C_Line_key) {
            promises.push(this.lineService.getLine(initialValues.Material.C_Line_key).then((line: Line) => {
                initialValues.cv_Taxon = line.cv_Taxon;
            }));
        }
        if (initialValues.Material.C_Taxon_key) {
            promises.push(this.animalService.getActiveCharacteristics(initialValues.Material.C_Taxon_key).then((characteristics: TaxonCharacteristic[]) => {
                initialValues.characteristics = characteristics;
            }));
        }

        // Get MaterialTypeKey for Animal Type Materials
        promises.push(this.materialService.getMaterialTypeKey('Animal').then((materialTypeKey: number) => {
            initialValues.C_MaterialType_key = materialTypeKey;
        }));

        return Promise.all(promises).then(() => {
            for (let i = 0; i < numberItemsToAdd; i++) {
                animals.push(this.createNewAnimal(initialValues));
            }

            return animals;
        });
    }

    createNewAnimal(initialValues: BulkCreateAnimal): Entity<Animal> {
        const newMaterial = this.materialService.create(initialValues);              
        newMaterial.MicrochipIdentifier = initialValues.Material.MicrochipIdentifier;
        newMaterial.ExternalIdentifier = initialValues.Material.ExternalIdentifier;
        const newAnimal = this.animalService.create();
        newAnimal.C_Sex_key = initialValues.C_Sex_key;
        newAnimal.C_Generation_key = initialValues.C_Generation_key;
        newAnimal.C_Diet_key = initialValues.C_Diet_key;
        newAnimal.ShipmentID = initialValues.ShipmentID;
        newAnimal.VendorID = initialValues.VendorID;
        newAnimal.C_Order_key = initialValues.C_Order_key;
        newAnimal.DateBorn = initialValues.DateBorn;
        newAnimal.DateExit = initialValues.DateExit;
        newAnimal.C_ExitReason_key = initialValues.C_ExitReason_key;
        newAnimal.PhysicalMarker = initialValues.PhysicalMarker;
        newAnimal.Owner = initialValues.Owner;
        newAnimal.AlternatePhysicalID = initialValues.AlternatePhysicalID;
        newAnimal.Comments = initialValues.Comments;
        newAnimal.Material = newMaterial;
        newAnimal.TaxonCharacteristics = [];
        newAnimal.HeldFor = initialValues.HeldFor;
        newAnimal.CITESNumber = initialValues.CITESNumber;

        if (initialValues.DateOrigin) {
            newAnimal.DateOrigin = initialValues.DateOrigin;
        } else {
            newAnimal.DateOrigin = this.requiredFields.includes('DateOrigin') ? null : new Date();
        }
        if (initialValues.C_AnimalStatus_key) {
            newAnimal.C_AnimalStatus_key = initialValues.C_AnimalStatus_key;
        } else {
            newAnimal.cv_AnimalStatus = initialValues.cv_AnimalStatus;
        }

        if (initialValues.cv_AnimalMatingStatus) {
            newAnimal.cv_AnimalMatingStatus = initialValues.cv_AnimalMatingStatus;
        }

        newAnimal.C_AnimalUse_key = initialValues.C_AnimalUse_key;
        newAnimal.C_BreedingStatus_key = initialValues.C_BreedingStatus_key;
        newAnimal.C_AnimalClassification_key = initialValues.C_AnimalClassification_key;
        newAnimal.C_IACUCProtocol_key = initialValues.C_IACUCProtocol_key;
        newAnimal.C_PhysicalMarkerType_key = initialValues.C_PhysicalMarkerType_key;
        newAnimal.Material.C_MaterialOrigin_key = initialValues.Material.C_MaterialOrigin_key;

        if (initialValues.Material.C_Line_key && initialValues.cv_Taxon) {
            newMaterial.cv_Taxon = initialValues.cv_Taxon;

            newMaterial.C_Line_key = initialValues.Material.C_Line_key;
            if (newAnimal.TaxonCharacteristicInstance) {
                this.animalLogic.deleteTaxons(newAnimal);
            }
            if (initialValues.TaxonCharacteristicInstance) {
                this.animalService.createTaxonCharacteristicsWithInitialValues(newAnimal, initialValues.TaxonCharacteristicInstance);
            }
        }
            
        if (initialValues.JobKey) {
            this.addJobMaterial(newAnimal, initialValues.JobKey);
        }
            
        if (initialValues.Genotype) {
            for (const genotype of initialValues.Genotype) {
                const assay = genotype.cv_GenotypeAssay.C_GenotypeAssay_key; 
                const symbol = genotype.cv_GenotypeSymbol.C_GenotypeSymbol_key;
                this.addGenotypeRecord(newAnimal, assay, symbol);
            }
        }
        return newAnimal;
    }

    setCharacteristicDefaults(animal: any, initialValues: any): any {
        if (!notEmpty(animal.TaxonCharacteristicInstance) ||
            !notEmpty(initialValues.TaxonCharacteristicInstance)
        ) {
            return animal;
        }

        for (const characteristic of animal.TaxonCharacteristicInstance) {
            if (initialValues.characteristicsDefaults[characteristic.C_TaxonCharacteristic_key]) {
                characteristic.CharacteristicValue = initialValues.characteristicsDefaults[characteristic.C_TaxonCharacteristic_key].CharacteristicValue;
            }
        }
        return animal;
    }

    addJobMaterial(animal: any, jobKey: number) {
        const materialKey = animal.C_Material_key;
        this.jobService.createJobMaterial({
            C_Material_key: materialKey,
            C_Job_key: jobKey,
            DateIn: new Date(),
            Version: 0
        });
    }

    addGenotypeRecord(animal: any, assayKey: any, symbolKey: any) {
        if (!assayKey) {
            return;
        }
        this.genotypeService.create({
            C_Material_key: animal.C_Material_key,
            C_GenotypeAssay_key: assayKey,
            C_GenotypeSymbol_key: symbolKey
        });
    }

    itemsToAddChanged(itemsToAdd: number) {
        // Changed the number of animals - start over
        this.bulkHousing.itemsToAdd = itemsToAdd;
        this.housingRebuildUnits();
    }

    initBulkHousing() {
        this.bulkHousing = new BulkHousing();

        const preferLocal = true;

        const promises: Promise<void>[] = [
            // Check if housing is automatically named.
            this.namingService.isHousingNamingActive().then((active: boolean) => {
                this.bulkHousing.namingActive = active;
            }),

            // Housing Type
            this.vocabularyService.getCV(
                'cv_MaterialPoolTypes', 'MaterialPoolType', preferLocal
            ).then((data) => {
                this.bulkHousing.materialPoolTypes = data;
            }),
            this.vocabularyService.getCVDefault('cv_MaterialPoolTypes').then((data) => {
                if (data && data.C_MaterialPoolType_key) {
                    this.bulkHousing.materialPoolTypeKey = data.C_MaterialPoolType_key;
                }
            }),

            // Container Type            
            this.locationService.getContainerTypes('Animal').then((data) => {
                this.bulkHousing.containerTypes = data;
            }),
            this.vocabularyService.getCVContainerTypeDefault('Animal').then((data) => {
                if (data && data.C_ContainerType_key) {
                    this.bulkHousing.containerTypeKey = data.C_ContainerType_key;
                }
            }),

            // Location
            this.locationService.getDefaultLocation().then((data) => {
                this.bulkHousing.location = data;
            }),

            this.vocabularyService.getCVDefault('cv_MaterialPoolStatuses').then((data) => {
                if (data && data.C_MaterialPoolStatus_key) {
                    this.bulkHousing.materialPoolStatusKey = data.C_MaterialPoolStatus_key;
                }
            })
        ];

        Promise.all(promises).then(() => {
            // All set, enable the checkbox
            this.bulkHousing.enabled = true;
        });
    }

    housingAddChanged() {
        // Distribute the animals
        this.housingRebuildUnits();
    }

    housingSizeChanged() {
        // Redistribute the animals
        this.housingRebuildUnits();
    }

    housingRebuildUnits() {
        if (!this.bulkHousing.add) {
            // Not adding housing, so nothing to do
            return;
        }

        if (!this.bulkHousing.size) {
            // Can't fill units without a size
            return;
        }

        const units = new Array<BulkHousingUnit>();

        // Distribute the animals by filling housing units.
        // Note: this leaves animals unhoused rather than partially fill a
        // housing unit. The user will need to decide what to do with those
        // unhoused animals.
        let items = this.bulkHousing.itemsToAdd;
        while (items >= this.bulkHousing.size) {
            // Fill up a new unit
            const unit = new BulkHousingUnit(this.bulkHousing.size);
            units.push(unit);

            // Account for the now-housed animals
            items -= unit.size;
        }

        // Use these new units
        this.bulkHousing.units = units;

        // Count the unhoused (again, this time officially)
        this.housingUpdateUnhoused();
    }

    housingUnitChanged(unit: BulkHousingUnit, index: number) {
        // Deal with typed-in negative numbers or clearing the input
        unit.size = Math.max(0, unit.size);

        // How many still left?
        this.housingUpdateUnhoused();

        if (this.bulkHousing.unhoused < 0) {
            // Too many animals have been moved into this housing
            unit.size += this.bulkHousing.unhoused;

            // Look for unhoused animals - just in case... should be 0.
            this.housingUpdateUnhoused();
        }
    }

    housingUpdateUnhoused() {
        // Add up all the housed animals
        const housed = this.bulkHousing.units.reduce(
            (sum: number, unit: BulkHousingUnit) => sum + unit.size
            , 0);

        // So, how many did not get a house?
        this.bulkHousing.unhoused = this.bulkHousing.itemsToAdd - housed;
    }

    housingAddUnit() {
        // Add a new unit, and try to fill it with the remaining animals
        this.bulkHousing.units.push(
            new BulkHousingUnit(Math.min(this.bulkHousing.unhoused, this.bulkHousing.size))
        );

        // See who else needs a house
        this.housingUpdateUnhoused();
    }

    housingDeleteUnit(unit: BulkHousingUnit, index: number) {
        // Remove the unit
        this.bulkHousing.units.splice(index, 1);

        // See if any animals got kicked out
        this.housingUpdateUnhoused();
    }

    async createNewHousings(bulkHousing: BulkHousing, newAnimals: any[]): Promise<MaterialPool[]> {
        if (!bulkHousing.add) {
            // Nothing to do
            return [];
        }

        // Make sure the animals are in a consistent order
        sortObjectArrayByProperty(newAnimals, 'C_Material_key', true);


       // Going to need to keep track of which animal is being housed.
        let animalIndex = 0;
        const newHousings: MaterialPool[] = [];
        for (const unit of bulkHousing.units) {
            // Create a new housing unit, aka MaterialPool
            const materialPool = this.materialPoolService.createMaterialPool({
                MaterialPoolID: unit.id,
                C_MaterialPoolType_key: bulkHousing.materialPoolTypeKey,
                C_ContainerType_key: bulkHousing.containerTypeKey,
                DatePooled: bulkHousing.date,
                Owner: bulkHousing.owner,
                C_MaterialPoolStatus_key: bulkHousing.materialPoolStatusKey
            });

            // Put the housing unit somewhere
            await this.locationService.createMaterialLocation({
                C_MaterialPool_key: materialPool.C_MaterialPool_key,
                C_LocationPosition_key: bulkHousing.location.C_LocationPosition_key,
            });

            // Put animals in this housing unit
            let items = unit.size;
            while (items > 0) {
                // Get the next animal
                const animal = newAnimals[animalIndex];
                if (!animal) {
                    // Ran out of animals... weird
                    break;
                }

                // Put the animal in the housing unit
                const mpm = this.materialPoolService.createMaterialPoolMaterial({
                    C_MaterialPool_key: materialPool.C_MaterialPool_key,
                    C_Material_key: animal.C_Material_key,
                    DateIn: bulkHousing.date,
                });
                animal.Material.MaterialPoolMaterial.unshift(mpm);

                // Account for the used-up space and housed animal
                items -= 1;
                animalIndex += 1;
            }
            newHousings.push(materialPool);
        }

        // All done
        return newHousings;
    }

    // <select> formatters
    materialPoolTypeKeyFormatter = (value: any) => {
        return value.C_MaterialPoolType_key;
    }
    materialPoolTypeFormatter = (value: any) => {
        return value.MaterialPoolType;
    }
    containerTypeKeyFormatter = (value: any) => {
        return value.C_ContainerType_key;
    }
    containerTypeFormatter = (value: any) => {
        return value.ContainerType;
    }
}
